import React from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Button,
    styled,
    useMediaQuery,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../Context/CardContext';

interface MethodDialogProps {
    open: boolean;
    onClose: () => void;
    method: {
        id: string;
        title: string;
        color: string;
        text: string;
        image_kachel: string;
        image_download: string;
    };
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxWidth: '800px',
        width: '90%',
        borderRadius: '12px',
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            width: '95%',
            margin: theme.spacing(1),
        }
    }
}));

const DialogHeader = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '400px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
        height: '300px',
    },
    [theme.breakpoints.down('sm')]: {
        height: '200px',
    },
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '16px',
    right: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    [theme.breakpoints.down('sm')]: {
        top: '8px',
        right: '8px',
    },
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)'
    }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    }
}));

const MethodDialog: React.FC<MethodDialogProps> = ({ open, onClose, method }) => {
    const { addItem } = useCart();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleAddToCart = () => {
        addItem({
            id: method.id,
            title: method.title,
            image: method.image_kachel,
            type: 'method'  // Wichtig: Hier setzen wir den Typ auf 'method'
        });
        onClose();
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogHeader>
                <img src={method.image_kachel} alt={method.title} />
                <CloseButton onClick={onClose} size={isMobile ? "small" : "medium"}>
                    <CloseIcon />
                </CloseButton>
            </DialogHeader>
            <DialogContent>
                <ContentContainer>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h2"
                        gutterBottom
                        sx={{
                            wordBreak: 'break-word',
                            hyphens: 'auto'
                        }}
                    >
                        {method.title}
                    </Typography>
                    <Typography
                        variant="body1"
                        paragraph
                        sx={{
                            fontSize: isMobile ? '0.9rem' : '1rem'
                        }}
                    >
                        {method.text}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        mt: isMobile ? 2 : 4
                    }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddToCart}
                            fullWidth={isMobile}
                            sx={{
                                borderRadius: '24px',
                                padding: isMobile ? '6px 16px' : '8px 32px',
                                fontSize: isMobile ? '0.875rem' : '1rem'
                            }}
                        >
                            In den Warenkorb
                        </Button>
                    </Box>
                </ContentContainer>
            </DialogContent>
        </StyledDialog>
    );
};

export default MethodDialog;